'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import LaunchIcon from '@mui/icons-material/Launch'
import { Alert, Box, Button, LinearProgress } from '@mui/material'
import { toast } from 'react-toastify'

import useWhatsappOfficialApi from '@/hooks/useWhatsappOfficialApi'
import { ROUTES, UTILS } from '@/shared/constants'

type MetaSignupPayload = {
  phoneNumberId: string
  wabaId: string
  businessId: string
  linkedPageId?: string
  linkedPageName?: string
  linkedPixelId?: string
  linkedPixelName?: string
}

type FacebookAuthResponse = {
  code?: string
}

type FacebookLoginResponse = {
  authResponse?: FacebookAuthResponse
  status?: string
}

type FacebookSdk = {
  init: (config: {
    appId: string
    autoLogAppEvents: boolean
    xfbml: boolean
    version: string
  }) => void
  login: (
    callback: (response: FacebookLoginResponse) => void,
    options: Record<string, unknown>
  ) => void
}

type MetaSignupMessage = {
  type?: string
  event?: string
  data?: Record<string, unknown>
}

const META_SIGNUP_FINISH_EVENTS = [
  'FINISH',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
]
const META_SIGNUP_ABORT_EVENTS = ['CANCEL', 'ERROR']

declare global {
  interface Window {
    FB?: FacebookSdk
    fbAsyncInit?: () => void
  }
}

const META_APP_ID = process.env.NEXT_PUBLIC_META_APP_ID ?? UTILS.BLANK
const META_EMBEDDED_SIGNUP_CONFIG_ID =
  process.env.NEXT_PUBLIC_META_EMBEDDED_SIGNUP_CONFIG_ID ?? UTILS.BLANK
const META_GRAPH_API_VERSION =
  process.env.NEXT_PUBLIC_META_GRAPH_API_VERSION ?? 'v25.0'
const FB_TRUSTED_ORIGINS = [
  'https://www.facebook.com',
  'https://web.facebook.com',
  'https://business.facebook.com',
]

function isFacebookOrigin(origin: string) {
  try {
    const hostname = new URL(origin).hostname

    return hostname === 'facebook.com' || hostname.endsWith('.facebook.com')
  } catch {
    return false
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function getObjectKeys(value: unknown) {
  return isRecord(value) ? Object.keys(value).slice(0, 30) : []
}

function parseMessageData(data: unknown): unknown {
  if (typeof data !== 'string') return data

  try {
    return JSON.parse(data)
  } catch {
    const params = new URLSearchParams(data)

    if (!Array.from(params.keys()).length) return data

    return Object.fromEntries(params.entries())
  }
}

function findStringValue(
  value: unknown,
  keys: string[],
  depth = 0
): string | undefined {
  if (!isRecord(value) || depth > 3) return undefined

  for (const key of keys) {
    const directValue = value[key]
    if (typeof directValue === 'string' && directValue.trim()) {
      return directValue
    }
  }

  for (const nestedValue of Object.values(value)) {
    const found = findStringValue(nestedValue, keys, depth + 1)
    if (found) return found
  }

  return undefined
}

function findNestedIdValue(
  value: unknown,
  containerKeys: string[],
  depth = 0
): string | undefined {
  if (!isRecord(value) || depth > 3) return undefined

  for (const key of containerKeys) {
    const nestedValue = value[key]
    if (isRecord(nestedValue)) {
      const id = nestedValue.id
      if (typeof id === 'string' && id.trim()) return id
    }
  }

  for (const nestedValue of Object.values(value)) {
    const found = findNestedIdValue(nestedValue, containerKeys, depth + 1)
    if (found) return found
  }

  return undefined
}

function parseSignupMessage(event: MessageEvent): MetaSignupMessage | null {
  if (!FB_TRUSTED_ORIGINS.includes(event.origin) && !isFacebookOrigin(event.origin)) {
    return null
  }

  let message: unknown
  try {
    message = parseMessageData(event.data)
  } catch {
    return null
  }

  if (!isRecord(message) || message.type !== 'WA_EMBEDDED_SIGNUP') return null

  return message as MetaSignupMessage
}

function parseSignupPayload(event: MessageEvent): MetaSignupPayload | null {
  if (!FB_TRUSTED_ORIGINS.includes(event.origin) && !isFacebookOrigin(event.origin)) {
    return null
  }

  let message: MetaSignupMessage
  try {
    message = parseMessageData(event.data) as MetaSignupMessage
  } catch {
    return null
  }

  if (!isRecord(message)) return null

  if (message.type !== 'WA_EMBEDDED_SIGNUP') return null

  const isFinishEvent = META_SIGNUP_FINISH_EVENTS.includes(message.event ?? '')
  const data = message.data
  const phoneNumberId =
    findStringValue(data, [
      'phone_number_id',
      'phoneNumberId',
      'phone_id',
      'phoneId',
      'whatsapp_phone_number_id',
      'whatsappPhoneNumberId',
    ]) ?? findNestedIdValue(data, ['phone_number', 'phoneNumber', 'phone'])
  const wabaId =
    findStringValue(data, [
      'waba_id',
      'wabaId',
      'whatsapp_business_account_id',
      'whatsappBusinessAccountId',
    ]) ??
    findNestedIdValue(data, [
      'waba',
      'whatsapp_business_account',
      'whatsappBusinessAccount',
    ])
  const businessId =
    findStringValue(data, [
      'business_id',
      'businessId',
      'business_manager_id',
      'businessManagerId',
    ]) ??
    findNestedIdValue(data, [
      'business',
      'business_manager',
      'businessManager',
    ])

  if (!isFinishEvent || !phoneNumberId || !wabaId || !businessId) {
    console.warn('Meta Embedded Signup sem IDs obrigatórios', {
      origin: event.origin,
      event: message.event,
      dataKeys: getObjectKeys(data),
      hasPhoneNumberId: Boolean(phoneNumberId),
      hasWabaId: Boolean(wabaId),
      hasBusinessId: Boolean(businessId),
    })
    return null
  }

  return {
    phoneNumberId,
    wabaId,
    businessId,
    linkedPageId: findStringValue(data, ['page_id', 'linkedPageId']),
    linkedPageName: findStringValue(data, ['page_name', 'linkedPageName']),
    linkedPixelId: findStringValue(data, ['pixel_id', 'linkedPixelId']),
    linkedPixelName: findStringValue(data, ['pixel_name', 'linkedPixelName']),
  }
}

/**
 * Abre o Cadastro Incorporado da Meta para conectar a empresa à API oficial.
 * O SDK devolve o code OAuth e o postMessage devolve os identificadores da
 * WABA/número; ambos são enviados ao backend para finalizar a integração.
 */
export default function CoexEmbeddedSignupButton({ label = 'Ativar API oficial do WhatsApp' }: { label?: string }) {
  const { finishCoexSignup } = useWhatsappOfficialApi()
  const signupPayloadRef = useRef<MetaSignupPayload | null>(null)
  const authCodeRef = useRef<string | null>(null)
  const isFinishingRef = useRef(false)
  const isLaunchingRef = useRef(false)
  const [isSdkReady, setIsSdkReady] = useState(
    typeof window !== 'undefined' && Boolean(window.FB)
  )
  const [isSaving, setIsSaving] = useState(false)

  const isConfigured = useMemo(
    () => Boolean(META_APP_ID && META_EMBEDDED_SIGNUP_CONFIG_ID),
    []
  )

  useEffect(() => {
    if (!isConfigured || window.FB) return

    window.fbAsyncInit = () => {
      window.FB?.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: META_GRAPH_API_VERSION,
      })
      setIsSdkReady(true)
    }

    const existingScript = document.getElementById('facebook-jssdk')
    if (existingScript) return

    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.async = true
    script.defer = true
    script.crossOrigin = 'anonymous'
    script.src = 'https://connect.facebook.net/pt_BR/sdk.js'
    document.body.appendChild(script)
  }, [isConfigured])

  const finishSignup = useCallback(
    async (code: string, payload: MetaSignupPayload) => {
      if (isFinishingRef.current) return

      isFinishingRef.current = true
      const saved = await finishCoexSignup({
        code,
        phoneNumberId: payload.phoneNumberId,
        wabaId: payload.wabaId,
        businessId: payload.businessId,
        linkedPageId: payload.linkedPageId,
        linkedPageName: payload.linkedPageName,
        linkedPixelId: payload.linkedPixelId,
        linkedPixelName: payload.linkedPixelName,
      })

      setIsSaving(false)
      authCodeRef.current = null
      signupPayloadRef.current = null
      isFinishingRef.current = false
      isLaunchingRef.current = false

      if (saved) {
        window.location.href = ROUTES.WHATSAPP_TEMPLATES.PATH
      }
    },
    [finishCoexSignup]
  )

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (isLaunchingRef.current && isFacebookOrigin(event.origin)) {
        const parsedData = parseMessageData(event.data)

        console.info('Meta Embedded Signup postMessage recebido', {
          origin: event.origin,
          dataType: typeof event.data,
          parsedType: isRecord(parsedData) ? parsedData.type : undefined,
          parsedEvent: isRecord(parsedData) ? parsedData.event : undefined,
          topLevelKeys: getObjectKeys(parsedData),
          dataKeys: isRecord(parsedData)
            ? getObjectKeys(parsedData.data)
            : [],
        })
      }

      const message = parseSignupMessage(event)

      if (message && META_SIGNUP_ABORT_EVENTS.includes(message.event ?? '')) {
        signupPayloadRef.current = null
        authCodeRef.current = null
        isLaunchingRef.current = false
        setIsSaving(false)

        if (message.event === 'ERROR') {
          toast.error('Cadastro incorporado da Meta retornou erro')
        }

        return
      }

      const payload = parseSignupPayload(event)
      if (payload) {
        signupPayloadRef.current = payload

        if (authCodeRef.current) {
          finishSignup(authCodeRef.current, payload)
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => window.removeEventListener('message', handleMessage)
  }, [finishSignup])

  const handleSignup = useCallback(() => {
    if (!window.FB) {
      toast.error('SDK da Meta ainda não foi carregado')
      return
    }

    setIsSaving(true)
    signupPayloadRef.current = null
    authCodeRef.current = null
    isFinishingRef.current = false
    isLaunchingRef.current = true

    window.FB.login(
      (response) => {
        const code = response.authResponse?.code
        const payload = signupPayloadRef.current

        if (!code) {
          setIsSaving(false)
          isLaunchingRef.current = false
          toast.error('Cadastro incorporado cancelado ou sem autorização')
          return
        }

        authCodeRef.current = code

        if (payload) {
          void finishSignup(code, payload)
          return
        }

        setTimeout(() => {
          if (authCodeRef.current === code && !signupPayloadRef.current) {
            setIsSaving(false)
            authCodeRef.current = null
            isLaunchingRef.current = false
            toast.error('Cadastro incorporado não retornou os IDs da conta')
          }
        }, 15000)
      },
      {
        config_id: META_EMBEDDED_SIGNUP_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      }
    )
  }, [finishSignup])

  if (!isConfigured) {
    return (
      <Alert severity="warning">
        A ativação está indisponível no momento. Entre em contato com o suporte para continuar.
      </Alert>
    )
  }

  return (
    <Box display="grid" gap={1}>
      {isSaving && <LinearProgress />}
      <Button
        variant="contained"
        startIcon={<LaunchIcon />}
        disabled={!isSdkReady || isSaving}
        onClick={handleSignup}
      >
        {label}
      </Button>
    </Box>
  )
}
